import React from 'react';
import { t } from '@lingui/core/macro';
import {
  Select,
  MenuItem,
} from '@mui/material';
import { Block } from '../classes/Block';
import {
  FontAxisConfig,
  STATIC_FONT_WEIGHT_AXIS,
  VariableFontConfig,
  clampFontAxisValue,
  getVariableFontConfig,
} from '../utils/fontHelpers';

interface FontWeightWidthControlsProps {
  fontName: string | undefined;
  weight: number | undefined;
  width: number | undefined;
  weightField: keyof Block;
  widthField: keyof Block;
  onChange: (field: keyof Block, value: number) => void;
  weightLabel: string;
  widthLabel: string;
  variableFontConfig?: VariableFontConfig;
}

const FontWeightWidthControls: React.FC<FontWeightWidthControlsProps> = ({
  fontName,
  weight,
  width,
  weightField,
  widthField,
  onChange,
  weightLabel,
  widthLabel,
  variableFontConfig: providedVariableFontConfig,
}) => {
  const variableFontConfig: VariableFontConfig | null =
    providedVariableFontConfig ?? getVariableFontConfig(fontName);

  if (!variableFontConfig) {
    return (
      <div className="form-group">
        <label>{weightLabel}</label>
        <div className="input-wrapper">
          <Select
            className="dropdown-menu-select"
            value={weight ?? 400}
            onChange={(e) =>
              onChange(
                weightField,
                clampFontAxisValue(e.target.value, STATIC_FONT_WEIGHT_AXIS)
              )
            }
            style={{ width: '92px', height: '34px' }}
          >
            <MenuItem value={400}>{t`Normal`}</MenuItem>
            <MenuItem value={700}>{t`Bold`}</MenuItem>
            <MenuItem value={900}>{t`Bolder`}</MenuItem>
          </Select>
        </div>
      </div>
    );
  }

  const weightAxis: FontAxisConfig = variableFontConfig.weightAxis;
  const widthAxis: FontAxisConfig | undefined = variableFontConfig.widthAxis;
  const resolvedWeight: number = clampFontAxisValue(weight, weightAxis);
  const resolvedWidth: number | null = widthAxis ? clampFontAxisValue(width, widthAxis) : null;

  return (
    <>
      <div className="form-group">
        <label>{weightLabel}</label>
        <div className="input-wrapper" style={{ gap: '8px' }}>
          <input
            type="range"
            min={weightAxis.min}
            max={weightAxis.max}
            step={weightAxis.step}
            value={resolvedWeight}
            onChange={(e) =>
              onChange(
                weightField,
                clampFontAxisValue(e.target.value, weightAxis)
              )
            }
            style={{ flex: 1, minWidth: 0, width: 'auto' }}
          />
          <input
            type="number"
            min={weightAxis.min}
            max={weightAxis.max}
            step={weightAxis.step}
            value={resolvedWeight}
            onChange={(e) =>
              onChange(
                weightField,
                clampFontAxisValue(e.target.value, weightAxis)
              )
            }
            style={{ width: '68px', flexShrink: 0 }}
          />
        </div>
      </div>
      {widthAxis && resolvedWidth !== null && (
        <div className="form-group">
          <label>{widthLabel}</label>
          <div className="input-wrapper" style={{ gap: '8px' }}>
            <input
              type="range"
              min={widthAxis.min}
              max={widthAxis.max}
              step={widthAxis.step}
              value={resolvedWidth}
              onChange={(e) =>
                onChange(
                  widthField,
                  clampFontAxisValue(e.target.value, widthAxis)
                )
              }
              style={{ flex: 1, minWidth: 0, width: 'auto' }}
            />
            <input
              type="number"
              min={widthAxis.min}
              max={widthAxis.max}
              step={widthAxis.step}
              value={resolvedWidth}
              onChange={(e) =>
                onChange(
                  widthField,
                  clampFontAxisValue(e.target.value, widthAxis)
                )
              }
              style={{ width: '68px', flexShrink: 0 }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FontWeightWidthControls;
